import SaveIcon from "@mui/icons-material/Save";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { skipToken } from "@reduxjs/toolkit/query";
import type { MRT_PaginationState as PaginationState } from "material-react-table";
import { enqueueSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import LoadingButton from "_shared/_views/loading/loading-button";
import { TemplateOwnershipScope } from "_shared/_views/templates/template-ownership-filter";
import TemplateSelector, { type TemplateSelectorRow } from "_shared/_views/templates/template-selector";
import EntityMember from "_shared/entity-members/entity-member";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import { getIndividualsByUserIds } from "marketplace/individuals/_stores/individuals-redux-slice";
import { useSession } from "users/__session/session-context";
import { EmbeddedTemplatePanelContent } from "work/fee-schedule-templates/_views/_shared/embedded-containers";
import {
  useCreateTeamTemplateMutation,
  useGetTeamTemplatesQuery,
} from "work/team-templates/_api/team-template-api";
import { TeamTemplateOrderField } from "work/team-templates/_api/queries/team-template-order-field";
import TeamTemplateQuery, {
  TeamTemplateFilterParameters,
  TeamTemplateOrderParameters,
} from "work/team-templates/_api/queries/team-template-query";
import {
  getIsLoadingTeamTemplatesByQuery,
  getTeamTemplatesByQuery,
} from "work/team-templates/_store/team-templates-redux-slice";
import { paneIsCompact } from "work/_views/work-pane-layout";
import TeamTemplate from "work/team-templates/team-template";
import { TeamTemplateParty } from "work/team-templates/team-template-party";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";
import VendorTeamName from "work/work-agreements/teams/vendor-team/vendor-team-name/vendor-team-name";

const INTERFACE_NAME = "ProposalClientTeamTemplatePanel";

/**
 * The shared container carries a fixed pixel width for the tabs that are not resizable; inside the
 * drag-resizable panel the content has to follow the column instead. Same reasoning as the Vendor
 * Team tab's own container.
 *
 * The height goes with it. `EmbeddedTemplatePanelContent` is a plain block sized by its contents,
 * and the panel below it ends in a paginator that MRT drops to the foot of its paper with a
 * `margin-top: auto` — which moves nothing unless the paper has height to spare. With this wrapper
 * between the column and the panel, the paginator sat directly under the last template instead of at
 * the bottom of the column, which is where the Fee Schedule tab's is; that tab has no wrapper here.
 */
const TemplateContainer = styled(EmbeddedTemplatePanelContent)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
  width: "100%",
  // The fill is for the column case only. On a compact pane this panel is a row in a stack rather
  // than a column beside the roster, and the stack's container has a definite height of its own — so
  // `height: 100%` handed the collapsed accordion the whole tab and left the roster under it with
  // nowhere to draw. See `CollapsibleTemplatePanel`'s `Panel`, which is harmless there for the
  // opposite reason: its parent has no definite height, so the percentage resolves to `auto`.
  [paneIsCompact(theme)]: {
    height: "auto",
  },
}));
const CreateTemplateAction = styled(Button)(({ theme }) => ({
  alignSelf: "flex-start",
  marginBottom: theme.spacing(1),
}));
// A person revealed under a template: what they are to it, then what they are called. The role
// travels with the person because a table of named rows has nowhere to put a subheader.
const PersonNameCell = styled("div")(({ theme }) => ({
  alignItems: "baseline",
  columnGap: theme.spacing(1),
  display: "flex",
  minWidth: 0,
  width: "100%",
}));
const PersonRole = styled("span")(({ theme }) => ({
  color: theme.palette.text.secondary,
  flexShrink: 0,
  fontSize: "0.7rem",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
}));
const PersonName = styled("span")(() => ({
  fontSize: "1em",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

/**
 * What a template row says about somebody the directory will not name. A membership is two GUIDs and
 * a GUID identifies nobody to a reader, so an unresolved person is described rather than identified —
 * and a row's name is what the panel sorts and searches on, so the identifier must not leak in
 * through the search string either (issue #4830).
 */
export const UNNAMED_TEMPLATE_PERSON = "Client team member";

export type ClientTeamTemplatePanelProps = {
  /** The Client Representative. A template never supplies or displaces one. */
  leader: EntityMember;
  /** The roster as it stands, leader excluded — the same list the roster table is drawn from. */
  members: readonly EntityMember[];
  /** True while a roster command is in flight; the panel offers nothing to press meanwhile. */
  busy: boolean;
  /** Whether the pane is too narrow to hold the panel open. */
  compact: boolean;
  /**
   * Whether this viewer may save the current roster as a template. Separate from being able to apply
   * one: a reader who may not change the roster can still be offered the templates they would apply
   * if they could, but has nothing of their own to save.
   */
  canCreateTemplate: boolean;
  /**
   * Reports the roster this panel means the client team to have.
   *
   * Nothing is written when it is called: the roster waits for the page's Save like every other
   * tab's edits (#5116), which is why there is no line of success copy to hand back with it.
   */
  onMembersChanged: (members: EntityMember[]) => void | Promise<void>;
};

/**
 * The Client Team tab's templates column: the panel the Conflicts and Vendor Team tabs draw, listing
 * the saved client-side rosters this viewer may reach.
 *
 * **It lists client templates because it asked the server for them**, through
 * `TeamTemplateFilterParameters.party`. Nothing here separates the two sides: a request that named no
 * party would come back holding vendor templates, not everything, because the filter defaults to
 * Vendor on the server as well. See `docs/software-design/template-panels.md`.
 *
 * No `tabs`: a team-template panel has one source to draw from and a strip with a single tab in it
 * says nothing. No `sorting`: the panel holds every row it is showing, so the table sorts them.
 *
 * **A tick is not stored anywhere.** A template reads as applied when every person it names is on the
 * roster, and the roster is the only truth there is — which is what stops the panel and the table
 * disagreeing after a realtime re-seed, and what keeps this out of `clientTeamTemplateIds`, a field
 * that already means something else entirely (which team templates the CLIENT applied while composing
 * the negotiable Vendor Team — see `proposal-command-builder.ts`).
 */
export default function ClientTeamTemplatePanel(props: Readonly<ClientTeamTemplatePanelProps>) {
  const { busy, canCreateTemplate, compact, leader, members, onMembersChanged } = props;

  const session = useSession();
  const userId = session.user?.id;
  const userLegalEntityId = session.user?.legalEntityId;

  const [ownershipScope, setOwnershipScope] = useState<TemplateOwnershipScope>("all");
  // The panel's own search box. It narrows the page in hand rather than the query, matching the
  // Vendor Team panel beside it so the two team panels answer a typed name the same way.
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: PaginationParameters.DEFAULT_PAGE_INDEX,
    pageSize: PaginationParameters.DEFAULT_PAGE_SIZE,
  });
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const templateQuery = useMemo(() => {
    const filterParameters = new TeamTemplateFilterParameters({
      creatorUserId: ownershipScope === "own" ? userId : undefined,
      // The whole of the client/vendor separation, as far as this component is concerned: it asks for
      // one side and is handed that side. It never receives the other and filters it out.
      party: TeamTemplateParty.Client,
    });
    const orderParameters = new TeamTemplateOrderParameters({
      orderBy: TeamTemplateOrderField.Name,
      orderDir: OrderDirection.Descending,
    });
    return new TeamTemplateQuery({
      filterParameters,
      orderParameters,
      // The paginator has to move the query, or pressing page two would redraw the page in hand and
      // call it the second one.
      paginationParameters: PaginationParameters.create(pagination),
    });
  }, [ownershipScope, pagination, userId]);

  const templatesApiQuery = useGetTeamTemplatesQuery({ query: templateQuery, interfaceName: INTERFACE_NAME });
  const [createTeamTemplate] = useCreateTeamTemplateMutation();
  const templates = useSelector(getTeamTemplatesByQuery(templateQuery));
  const isLoadingTemplates =
    useSelector(getIsLoadingTeamTemplatesByQuery(templateQuery)) ||
    templatesApiQuery.isLoading ||
    templatesApiQuery.isFetching;

  const templatePeopleUserIds: Set<string> = useMemo(() => {
    const ids = new Set<string>();
    for (const template of templates?.data ?? []) {
      ids.add(template.leader.userId.value);
      for (const member of template.members) ids.add(member.userId.value);
    }
    return ids;
  }, [templates]);
  const individuals = useSelector(getIndividualsByUserIds(templatePeopleUserIds));
  const missingUserIds = useMemo(() => {
    const loaded = new Set(individuals.map((individual) => individual.userId.value));
    const missing = new Set<string>();
    templatePeopleUserIds.forEach((id) => {
      if (!loaded.has(id)) missing.add(id);
    });
    return missing;
  }, [individuals, templatePeopleUserIds]);
  const individualsQuery = useGetIndividualsByUserIdsQuery(
    missingUserIds.size > 0 ? { userIds: missingUserIds } : skipToken,
  );
  const individualsByUserId = useMemo(
    () => new Map(individuals.map((individual) => [individual.userId.value, individual])),
    [individuals],
  );

  const rosterUserIds = useMemo(
    () => new Set(members.map((member) => member.userId.value)),
    [members],
  );
  const searchText = search.trim().toLocaleLowerCase();
  const visibleTemplates = (templates?.data ?? []).filter(
    (template) => !searchText || template.name.value.toLocaleLowerCase().includes(searchText),
  );

  // How many of the templates in hand read as applied, for the panel's collapsed summary. Derived on
  // every render from the roster, like every other tick here, so there is nothing that can go stale.
  const appliedCount = (templates?.data ?? []).filter((template) =>
    templateIsApplied(template, leader, rosterUserIds),
  ).length;

  function personName(member: EntityMember): string {
    return individualsByUserId.get(member.userId.value)?.fullName ?? UNNAMED_TEMPLATE_PERSON;
  }

  async function togglePerson(member: EntityMember) {
    if (member.userId.isEqualTo(leader.userId)) return;
    if (rosterUserIds.has(member.userId.value)) {
      await onMembersChanged(
        members.filter((existing) => !existing.userId.isEqualTo(member.userId)).map((existing) => existing.clone()),
      );
      return;
    }
    await onMembersChanged([...members.map((existing) => existing.clone()), member.clone()]);
  }

  async function toggleTemplate(template: TeamTemplate) {
    const people = templatePeople(template, leader);
    if (people.length === 0) return;
    const applied = templateIsApplied(template, leader, rosterUserIds);
    const templateUserIds = new Set(people.map((person) => person.userId.value));
    if (applied) {
      await onMembersChanged(
        members
          .filter((existing) => !templateUserIds.has(existing.userId.value))
          .map((existing) => existing.clone()),
      );
      return;
    }
    const additions = people.filter((person) => !rosterUserIds.has(person.userId.value));
    await onMembersChanged([
      ...members.map((existing) => existing.clone()),
      ...additions.map((person) => person.clone()),
    ]);
  }

  /** The people a template would put on the roster, revealed under it — or a loader, while their names are on their way. */
  function buildPersonRows(template: TeamTemplate, templateKey: string): TemplateSelectorRow[] {
    // Braced rather than a one-line early return on purpose. Written on one line, the swc-to-istanbul
    // source map drops the whole guard, so coverage reports this as exercised whether or not anything
    // ever renders the loader.
    if (individualsQuery.isLoading) {
      return [
        {
          id: `${templateKey}-people-loading`,
          name: "Loading people...",
          content: <TemplatePersonCell name="Loading people..." teamRole="" />,
          selected: false,
          disabled: true,
          onSelectedChanged: () => undefined,
        },
      ];
    }
    const everyone = [template.leader, ...template.members];
    return everyone.map((member, index) => {
      const isRepresentative = member.userId.isEqualTo(leader.userId);
      const name = personName(member);
      return {
        id: `${templateKey}-person-${member.userId?.value ?? index}`,
        name,
        content: <TemplatePersonCell name={name} teamRole={member.isEqualTo(template.leader) ? "Leader" : "Member"} />,
        // Somebody who is already the Client Representative reads as ticked and is locked: the
        // representative leads the roster rather than sitting on it, and that is not the reader's to
        // unpick.
        selected: isRepresentative || rosterUserIds.has(member.userId.value),
        disabled: isRepresentative || busy,
        disabledReason: isRepresentative ? "Already the Client Representative" : undefined,
        onSelectedChanged: () => void togglePerson(member),
      };
    });
  }

  function buildTemplateRow(template: TeamTemplate): TemplateSelectorRow {
    const templateKey = template.id?.value ?? template.name.value;
    const people = templatePeople(template, leader);
    const applied = templateIsApplied(template, leader, rosterUserIds);
    return {
      id: templateKey,
      name: template.name.value,
      selected: applied,
      // Some of its people are on the roster without the template itself being applied — through
      // another template, or added one at a time.
      indeterminate: !applied && people.some((person) => rosterUserIds.has(person.userId.value)),
      disabled: busy || people.length === 0,
      disabledReason: people.length === 0 ? "Everyone on this template already leads the team" : undefined,
      children: buildPersonRows(template, templateKey),
      onSelectedChanged: () => void toggleTemplate(template),
    };
  }

  function handleCreateClicked() {
    setCreateName("");
    setIsCreateDialogOpen(true);
  }

  function handleCreateDialogClosed() {
    if (isCreating) return;
    setIsCreateDialogOpen(false);
  }

  async function handleCreateConfirmed() {
    const name = createName.trim();
    if (!name || !userLegalEntityId) return;
    setIsCreating(true);
    const created = await saveClientTeamTemplate(createTeamTemplate, {
      entityId: userLegalEntityId.value,
      name,
      leader,
      members: [...members],
      creatorType: session.accountType,
    });
    setIsCreating(false);
    if (!created) return;
    setIsCreateDialogOpen(false);
    void templatesApiQuery.refetch();
  }

  function renderCreateAction() {
    if (!canCreateTemplate) return undefined;
    return (
      <Tooltip title="Save this client team as a template">
        <span>
          <CreateTemplateAction
            size="small"
            startIcon={<SaveIcon />}
            variant="outlined"
            loading={isCreating}
            disabled={isCreating || busy}
            onClick={handleCreateClicked}
          >
            Create Template
          </CreateTemplateAction>
        </span>
      </Tooltip>
    );
  }

  return (
    <>
      <TemplateContainer aria-label="Client team templates" role="group">
        <TemplateSelector
          action={renderCreateAction()}
          collapsedLabel="Client Team Templates"
          compact={compact}
          ownership={{
            label: "client team templates",
            value: ownershipScope,
            onChange: setOwnershipScope,
          }}
          pagination={{
            onChange: setPagination,
            rowCount: templates?.totalElements ?? 0,
            value: pagination,
          }}
          rows={visibleTemplates.map(buildTemplateRow)}
          search={{ onChange: setSearch, value: search }}
          selectedCount={appliedCount}
          status={{
            errorMessage: getErrorMessageFromUnknown(templatesApiQuery.error),
            isError: templatesApiQuery.isError,
            isFetching: isLoadingTemplates,
            isLoading: templatesApiQuery.isLoading,
            onRetry: () => void templatesApiQuery.refetch(),
          }}
          subject="client team templates"
        />
      </TemplateContainer>
      <Dialog fullWidth maxWidth="xs" open={isCreateDialogOpen} onClose={handleCreateDialogClosed}>
        <DialogTitle>Create Client Team Template</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Template Name"
            margin="dense"
            value={createName}
            onChange={(event) => setCreateName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") void handleCreateConfirmed();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={isCreating} onClick={handleCreateDialogClosed}>
            Cancel
          </Button>
          <LoadingButton loading={isCreating} disabled={!createName.trim()} onClick={handleCreateConfirmed}>
            Create
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

/** A person under a template: what they are to it, then what they are called. */
function TemplatePersonCell(props: Readonly<{ name: string; teamRole: string }>) {
  return (
    <PersonNameCell>
      {props.teamRole && <PersonRole>{props.teamRole}</PersonRole>}
      <PersonName>{props.name}</PersonName>
    </PersonNameCell>
  );
}

/**
 * The people a template would actually put on this roster.
 *
 * The Client Representative is excluded wherever a template names them, because they lead the team
 * rather than sitting on it — `ClientTeam.ReplaceMembersAsync` refuses a member list containing the
 * leader outright, so a template that named them and was applied whole would be rejected by the API.
 */
export function templatePeople(template: TeamTemplate, leader: EntityMember): EntityMember[] {
  return [template.leader, ...template.members].filter(
    (person) => !person.userId.isEqualTo(leader.userId),
  );
}

/**
 * Whether a template reads as applied: every person it would contribute is on the roster.
 *
 * Derived rather than stored. A template that named only the representative contributes nobody and
 * can never read as applied, which is why the caller disables its row instead.
 */
export function templateIsApplied(
  template: TeamTemplate,
  leader: EntityMember,
  rosterUserIds: ReadonlySet<string>,
): boolean {
  const people = templatePeople(template, leader);
  if (people.length === 0) return false;
  return people.every((person) => rosterUserIds.has(person.userId.value));
}

/**
 * Sends one create and reports the template it made, or undefined.
 *
 * At module scope because it holds a `try`/`catch` around an `await` — a component that contains one
 * is emitted uncompiled in its entirety, memoization and all (see app/CLAUDE.md). `saveTeamTemplate`
 * in `team-template-form.tsx` is at module scope for the same reason.
 */
async function saveClientTeamTemplate(
  createTeamTemplate: ReturnType<typeof useCreateTeamTemplateMutation>[0],
  request: {
    entityId: string;
    name: string;
    leader: EntityMember;
    members: EntityMember[];
    creatorType: Parameters<ReturnType<typeof useCreateTeamTemplateMutation>[0]>[0]["creatorType"];
  },
): Promise<TeamTemplate | undefined> {
  try {
    const response = await createTeamTemplate({
      entityId: request.entityId,
      // `VendorTeam` is the create request's leader-plus-members carrier rather than a statement that
      // this is a vendor team — `party` below is what says which side it is, and the server records
      // it. The two other callers of this mutation already hold a VendorTeam for their own reasons,
      // so the carrier stays as it is; `TeamTemplate.AsVendorTeam` is where a client template is
      // stopped from being read back as one.
      team: new VendorTeam({ leader: request.leader, members: request.members }),
      teamName: new VendorTeamName(request.name),
      creatorType: request.creatorType,
      party: TeamTemplateParty.Client,
    }).unwrap();
    enqueueSnackbar("Client team template created", { variant: "success" });
    return TeamTemplate.fromJSON(response);
  } catch (error) {
    console.error("Failed to create client team template", error);
    enqueueSnackbar("Failed to create client team template. Please try again.", { variant: "error" });
    return undefined;
  }
}
